import type { MetaFunction } from "@remix-run/react"
import {
  articleBodyStyle,
  articleFaqBodyStyle,
  articleSection,
  bold,
  faqGrid,
  h1Style,
  h1Wrap,
  h2Style,
  h3Style,
  headerTextStyle,
  highlightHead,
  imageGrid,
  imageGridBody,
  imagePreview2,
  orderedList,
  imageGridTitle,
  stepSectionStyle,
  unorderedList,
  faqH3Style,
  articleCompareImages,
  articleImageStyle,
  figCaptionStyle,
} from "./article.css"
import Footer from "~/components/Footer"
import Link from "~/components/Link"
import { golden, vars } from "~/components/styles/theme.css"
import { page } from "~/shared/styles.css"
import LinkButton from "~/components/LinkButton"
import { btnWrap } from "./index.css"
import Logo from "~/components/Logo"
import StickyHeader from "~/components/StickyHeader"
import {
  ActionBtn,
  Bold,
  Figure,
  H2,
  H3,
  Image,
  P,
  Section,
} from "~/components/articleComponents"
import ScrollyWolly from "~/components/ScrollyWolly"

export type PreviewImage = {
  url: string
  size: { w: number; h: number }
}

export type Url = {
  url: string
  initial?: PreviewImage | null
  loading?: boolean
}

const TITLE = "AI Garden Design Ideas | Instantly Visualise Garden Styles"
const DESCRIPTION =
  "Use AI garden design tools to generate beautiful planting ideas, borders, colour schemes and garden styles in seconds. Get instant tailored inspiration."
const URL = "https://rescapeai.com/ai-garden-design"
const IMAGE = "https://rescapeai.com/articles/firepit-space.webp"

export const meta: MetaFunction = () => {
  return [
    {
      title: TITLE,
    },
    {
      name: "description",
      content: DESCRIPTION,
    },
    {
      property: "og:title",
      content: TITLE,
    },
    {
      property: "og:description",
      content: DESCRIPTION,
    },
    {
      property: "og:image",
      content: IMAGE,
    },
    {
      tagName: "link",
      rel: "canonical",
      href: URL,
    },
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: TITLE,
        description: DESCRIPTION,
        author: {
          "@type": "Organization",
          name: "Rescape AI",
        },
        datePublished: "2025-10-22",
        dateModified: "2025-10-22",
        publisher: {
          "@type": "Organization",
          name: "Rescape AI",
          logo: {
            "@type": "ImageObject",
            url: "https://rescapeai.com/logo.webp",
          },
        },
        image: IMAGE,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": URL,
        },
      },
    },
  ]
}

function Index() {
  return (
    <>
      <div
        className={h1Style}
        style={{
          textAlign: "center",
          backgroundImage: "url(/articles/cottage-garden-bench-2.webp)",
        }}
      >
        <div className={h1Wrap}>
          <Logo width="108px" />

          <h1 className={headerTextStyle}>
            Rescape AI Garden Design - Smart Landscaping Made Simple
          </h1>
        </div>
      </div>
      <StickyHeader status="SignUp" />
      <div className={page}>
        <Section>
          <P>Updated Dec 23rd 2025</P>
          <P>
            AI garden design uses machine learning to transform a photo of your outdoor
            space into realistic visual concepts—showing you how different styles and
            layouts could look before you commit to any changes. Instead of imagining what
            a contemporary patio or cottage-style border might look like, you see it
            rendered directly onto your actual garden in seconds.
          </P>
          <P>
            This guide covers how AI garden design works, what features matter most in a
            quality tool, and how to move from a single photo to multiple design
            directions you can compare and refine.
          </P>
          <Figure
            src="/how-to-design-a-contemporary-garden/fourth-ai-garden-design-with-stone-paving-area.webp"
            caption="Contemporary garden design, a modern and pretty take with irregular patio layout creates an atmosphere of awe"
            style={{ marginTop: "1rem" }}
          />
        </Section>
        <Section>
          <H2>What is AI garden design?</H2>
          <P>
            AI garden design takes a photo of your outdoor space and transforms it into
            realistic visual concepts—showing you what different styles and layouts could
            look like before you make any changes. You upload an image of your current
            garden, yard, or patio, and the AI analyzes the space to generate design ideas
            tailored to that specific area.{" "}
          </P>
          <P>The process works like this:</P>
          <ul className={unorderedList}>
            <li>
              <Bold>Photo-based input:</Bold> You snap a picture of your existing outdoor
              space with your smartphone
            </li>
            <li>
              <Bold>Style selection:</Bold> You pick from a range of garden
              styles—contemporary, cottage, mediterranean, urban and others
            </li>
            <li>
              <Bold>Instant output:</Bold> The AI generates realistic design concepts in
              seconds
            </li>
          </ul>
          <figure style={{ marginBottom: "2rem" }}>
            <div className={articleCompareImages}>
              <img
                src={"/guide/garden-2-before-ai-design.webp"}
                alt={"Garden before redesign"}
                loading="lazy"
              />
              <img
                src={"/guide/bc861264-b11a-4bf0-b2ce-edb0594bec081.webp"}
                alt={"A cottage garden idea generated by Rescape AI"}
                loading="lazy"
              />
            </div>
            <img
              className={articleImageStyle}
              src={"/guide/bc861264-b11a-4bf0-b2ce-edb0594bec081.webp"}
              alt={"cottage garden design idea"}
              loading="lazy"
              style={{
                height: "320px",
                width: "100%",
                objectFit: "cover",
                objectPosition: "0% 80%",
                marginTop: "2rem",
              }}
            />
            <figcaption className={figCaptionStyle}>
              A soft, sunlit cottage-style patio filled with blooming flowers and vintage
              white furniture, creating a cozy corner for quiet mornings or afternoon tea.
            </figcaption>
          </figure>
          <P>
            Tools like Rescape AI make this accessible to anyone, regardless of design
            background. Rather than trying to imagine how a Japanese-inspired corner or
            modern garden might look, you see it rendered directly onto your actual space.
            Think of it as a creative brainstorming partner that works at digital speed.
          </P>
        </Section>
        <Section>
          <H2>Why use AI to design your garden</H2>
          <P>
            Traditional garden planning often involves hiring a landscape architect,
            waiting weeks for concepts, and paying substantial fees before seeing a single
            idea. For many homeowners, that process feels out of reach—or simply more
            involved than what's needed for a backyard refresh.
          </P>
          <P>AI garden design changes the equation in a few key ways:</P>
          <ul className={unorderedList}>
            <li>
              <Bold>Speed:</Bold> You generate multiple design ideas in seconds instead of
              waiting for professional consultations
            </li>
            <li>
              <Bold>Affordability:</Bold> You explore dozens of variations without the
              cost of traditional design services
            </li>
            <li>
              <Bold>Flexibility:</Bold> You try completely different looks for the same
              space and compare them side by side
            </li>
            <li>
              <Bold>Confidence:</Bold> You see realistic "after" images before committing
              to any physical changes
            </li>
          </ul>
        </Section>
        <ScrollyWolly
          before={{
            image: "/articles/before-ai-yard-design-patio.webp",
            title: "Your Backyard",
            alt: "A functional backyard with zero ambition",
          }}
          after={{
            image: "/articles/ai-yard-design-patio-and-flowerbeds.webp",
            title: "AI generated",
            alt: "Our AI landscape design realises the customers vision with beautiful paving opportunities and even recommends a better set of chairs...",
          }}
          height="478px"
          // marginBottom="2rem"
        />
        <Section>
          <P>
            Here's the thing—you might not know exactly what you want, and that's
            perfectly fine. AI tools let you experiment freely, testing styles you might
            never have considered until you see them applied to your own garden.
          </P>
        </Section>
        <Section>
          <H2>Key features of the best AI garden design generator</H2>
          <P>
            Not all AI garden tools deliver the same results. Some produce vague or
            unrealistic output, while others give you genuinely useful visualizations. The
            features below separate high-quality generators from basic options.
          </P>
          <H3>Photo-based AI garden design from your real space</H3>
          <P>
            The most effective AI garden design tools start with your actual outdoor
            space, not a generic template. You photograph your garden as it exists today,
            and the AI uses that image as the foundation for every design it generates.
          </P>
          <P>
            This approach grounds the results in reality. You see what changes would
            genuinely look like in your specific setting—with your fence, your trees, your
            patio dimensions. Rescape AI builds every concept directly from your uploaded
            photo, so the output reflects your space rather than someone else's.
          </P>
          <H3>Generate multiple garden variations quickly</H3>
          <P>
            Speed matters when you're exploring ideas. Waiting days between concepts slows
            down the creative process and makes comparison difficult.
          </P>
          <P>
            The best tools let you generate design after design in seconds. You can try
            formal layouts, cottage-style planting,{" "}
            <Link
              href={"/garden-design-guide/contemporary/features-of-contemporary-gardens"}
            >
              contemporary hardscaping
            </Link>
            , or wildlife-friendly features without pausing between each variation. This
            rapid iteration often helps you discover directions you wouldn't have
            considered otherwise.
          </P>
          <img
            className={articleImageStyle}
            src={"/articles/ai-yard-design-contemporary-minimalist-design.webp"}
          />
          <H3>Realistic high-quality landscape visualizations</H3>
          <P>
            Quality varies significantly across AI tools. Some produce cartoonish sketches
            that make it hard to evaluate whether a style actually suits your space.
            Others generate high-definition, realistic renderings that look closer to
            actual photographs.
          </P>
          <P>
            The more realistic the output, the easier it becomes to make confident
            decisions about your garden's direction.
          </P>
          <H3>Affordable AI garden planning for any budget</H3>
          <P>
            <Link href={"/ai-landscape-design"}>Professional landscape design</Link> can
            cost hundreds or thousands of dollars for initial concepts alone. AI garden
            planners offer a different model—credit-based pricing that lets you generate
            many designs without a large upfront investment.
          </P>
          <P>
            Rescape AI, for example, offers packages starting from a few dollars, making
            experimentation genuinely accessible for homeowners who want to explore
            options before committing to bigger expenses.
          </P>
        </Section>
        <Section>
          <H2>How to use AI garden design from photo in 3 steps</H2>
          <P>
            Getting started with AI garden design takes only a few minutes. The process is
            straightforward, even if you've never used design software before.
          </P>
          <H3>Step 1. Upload a photo of your outdoor space</H3>
          <P>
            Grab your smartphone and photograph the area you want to redesign. This could
            be your front yard, backyard, patio, balcony, or even a small patch of soil
            you've been meaning to improve.
          </P>
          <P>
            No professional photography skills required—a clear, well-lit image works
            perfectly. Natural daylight typically produces the best results.
          </P>
          <H3>Step 2. Choose your garden style and preferences</H3>
          <P>
            Next, select from available design styles. Options typically include
            contemporary, cottage, formal,{" "}
            <Link href={"/garden-design-guide/zen/zen-garden-ideas-using-ai"}>
              Japanese-inspired
            </Link>
            , Mediterranean, wildlife-friendly, and many more.
          </P>
          <img
            className={articleImageStyle}
            src="/marketing/generate-page-screenshot.webp"
            style={{ marginTop: "1rem", marginBottom: "1rem" }}
          />
          <P>
            You can try <Link href={"/garden-design-guide"}>different styles</Link> using
            the same photo, which makes comparison easy. If you're unsure what appeals to
            you, this is a good opportunity to experiment with
            <Link href={"/garden-design-app"}>options you haven't considered</Link>
            before.
          </P>
          <H3>Step 3. Generate and compare AI design ideas</H3>
          <P>
            Click generate and watch the AI produce design concepts in seconds. Review the
            results, then generate additional variations if you want to explore further.
          </P>
          <P>
            Compare options side by side until you find inspiration that resonates with
            your vision. The goal isn't necessarily to find a perfect final design—it's to
            clarify your direction and gather ideas you can refine or share with
            contractors.
          </P>
          <P>
            <Link href={"/hello/signup"}>
              Start generating garden designs from your photo
            </Link>
          </P>
        </Section>
        <Section>
          <H2>AI garden design for every outdoor space</H2>
          <P>
            AI garden tools handle far more than traditional backyard lawns. The
            technology adapts to various project types and space sizes, from sprawling
            properties to compact urban balconies.
          </P>
          <H3>AI front yard design for curb appeal</H3>
          <P>
            Your <Link href={"/ai-yard-design"}>front yard</Link> creates the first
            impression of your home. AI can help you visualize lawn alternatives, new
            pathway layouts, updated planting beds, or complete style transformations—all
            rendered onto your existing front-yard photo.
          </P>
          <P>
            This is particularly useful if you're considering changes like replacing grass
            with drought-tolerant landscaping or adding a welcoming entrance garden.
          </P>
          <H3>Backyard AI designer for patios and lawns</H3>
          <P>
            <Link href={"/ai-backyard-design"}>
              Backyards often serve multiple purposes:
            </Link>{" "}
            relaxation, entertaining, play areas, gardening. That versatility can make
            planning tricky—how do you balance competing priorities?
          </P>
          <P>
            AI tools let you experiment with patio configurations, pergola placements,
            lawn shapes, and seating arrangements. You can test different layouts without
            committing to any single direction, which helps clarify what matters most for
            your space.
          </P>
          <H3>AI flower bed design and borders</H3>
          <P>
            Flower beds and borders benefit from visual planning. Colors, heights, and
            textures interact in ways that can be hard to predict from plant labels alone.
          </P>
          <Figure
            src="/articles/english-garden-herbacious.webp"
            caption="Style: English, Feature: Herbaceous borders with perrenials, Creativity Level; 80%. AI generated gardens space full of blooming flowers."
            style={{ marginTop: "1rem", marginBottom: "2rem" }}
          />
          <P>
            You can test different planting arrangements, border shapes, and color schemes
            to see how they'd look in your actual garden before purchasing plants. This
            reduces the risk of expensive mistakes and helps you create more cohesive
            designs.
          </P>
          <H3>AI garden design for balconies and small spaces</H3>
          <P>
            Limited square footage doesn't mean limited options. AI garden design works
            for balconies, courtyards, and{" "}
            <Link href={"/garden-design-guide/urban/features-of-urban-gardens"}>
              compact urban spaces
            </Link>
            —helping renters and city dwellers visualize container gardens, vertical
            planting, and small-scale transformations.
          </P>
          <P>
            Even a few square meters can become a meaningful outdoor retreat with the
            right approach.
          </P>
        </Section>
        <Section>
          <H2>Free AI garden design vs paid options</H2>
          <P>
            You might be wondering whether free AI garden design tools can meet your
            needs. The answer depends on what you're trying to accomplish.
          </P>
          <ul className={unorderedList}>
            <li>
              <Bold>Free Tools:</Bold> Often useful for a quick trial or generating a
              single design idea. Expect limitations like watermarks, lower image quality,
              or restricted style options.
            </li>
            <li>
              <Bold>Paid Tools:</Bold> Typically offer more credits, higher-quality
              output, broader style selection, and the ability to iterate extensively
              until you find the right direction.
            </li>
          </ul>
          <P>
            If you're casually curious, a free option might suffice for initial
            exploration. If you're seriously planning changes and want to compare multiple
            concepts, paid tools generally deliver better value. The ability to generate
            many variations—and refine your direction over time—often makes the difference
            between vague inspiration and actionable plans.
          </P>
        </Section>
        <Section>
          <H2>Start designing your garden with AI today</H2>
          <P>
            AI garden design puts professional-quality visualization within reach. You
            photograph your space, choose styles that interest you, and generate realistic
            concepts in seconds—no design expertise required, no lengthy wait times.
          </P>
          <Figure
            src="/articles/firepit-space.webp"
            caption="Style: 'Small', Feature: 'A firepit space', Creativity Level: Dream mode. AI generated garden space with a firepit situated at the center."
            style={{ marginTop: "1rem", marginBottom: "2rem" }}
          />
          <P>
            Whether you're reimagining a front yard, planning a patio transformation, or
            simply exploring what's possible, AI tools let you experiment freely and
            compare options until you find inspiration worth pursuing.
          </P>
          <Link href={"/hello/signup"}>Create your AI garden design with Rescape AI</Link>
        </Section>
        <Section>
          <H2>Frequently asked questions about AI garden design</H2>
          <div className={faqGrid}>
            <div>
              <h3 className={faqH3Style}>
                Is there a free AI garden design tool available online?
              </h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                Yes, several AI garden design tools offer free trials or limited free
                generations. Rescape AI lets you start generating designs from your own
                photo with affordable credit packages, making it easy to explore without a
                large upfront commitment.
              </p>
            </div>
            <div>
              <h3 className={faqH3Style}>
                How accurate are AI-generated garden design images?
              </h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                AI-generated designs serve as inspiration and visualization aids rather
                than exact construction blueprints. They give you a realistic sense of how
                different styles could look in your actual space, helping you make more
                confident decisions before starting physical work.
              </p>
            </div>
            <div>
              <h3 className={faqH3Style}>What is the 70/30 rule in garden design?</h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                The 70/30 rule suggests using approximately 70% of one dominant
                element—typically greenery or a primary material—and 30% accent features
                for visual balance. AI tools can help you visualize layouts that follow
                this principle by generating designs with varied proportions.
              </p>
            </div>
          </div>
        </Section>
        <Section>
          <ActionBtn>Get Started</ActionBtn>
        </Section>
        <Footer showLinks={false} />
      </div>
    </>
  )
}

export default Index
